import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import { MARTINUS_ABAC_PAYLOAD } from '../abac-payloads/MartinusAbacPayload.js';
import { MartinusAccessController } from '../api/MartinusAccessController.js';

/**
 * Evaluates an ABAC action for a baseql operation which acts on another person. The person is loaded
 * from the argument named `viewedUserIDArgument`, so the policies can judge the relation between the
 * calling user and the person whose data is touched - the same evaluation the client uses to decide
 * whether to offer the operation at all.
 * @param action the action to evaluate, e.g. `UserEmployeeDetailsActions.EDIT_LAK`
 * @param viewedUserIDArgument the name of the operation parameter holding the ID of the viewed person
 * @throws if the action is not allowed for the current user on the viewed person
 */
export const checkAbacOnViewedUserMiddleware =
  (action: string, viewedUserIDArgument: string): BaseQLMiddleware =>
  async options => {
    if (context.serverContext) return options.next();

    const currentUser = await getMyself<Person>()({
      _id: 1,
      roles: { name: 1, contexts: 1, permissions: 1 },
      sorgeberechtigteKinderIDs: 1,
      spendenKinderIDs: 1,
    });
    if (!currentUser) throw new ExpectedError('Not authenticated');

    /**
     * Read the viewed person raw, as the permission evaluation must not depend on what the current
     * user is allowed to see.
     */
    const viewedUserID = options.args[viewedUserIDArgument] as string | undefined;
    const userCol = await getRawCollection<EntityData<Person>>(Person._collectionName);
    const viewedUser = viewedUserID
      ? await userCol.findOne(
          { _id: viewedUserID },
          { projection: { _id: 1, roles: 1, sorgeberechtigteKinderIDs: 1, spendenKinderIDs: 1 } },
        )
      : null;

    const answer = await MartinusAccessController.evaluate({
      type: MARTINUS_ABAC_PAYLOAD,
      action,
      currentUser,
      viewedUser,
    } as UserOnUserPayload);

    if (!answer.allowed) throw new ExpectedError(answer.reason);
    return options.next();
  };
